import { redactLabel } from './redact';
import type { AnalyticsProperties } from './types';

/**
 * Opt out of capture for an element and everything inside it. Put it on any
 * subtree that renders something we should not be reading at all.
 */
export const ANALYTICS_IGNORE_ATTRIBUTE = 'data-analytics-ignore';

/**
 * Give a control an explicit, stable name for analytics. Always preferred over
 * anything inferred, and the only way to get a meaningful name onto a control
 * whose visible text is record data.
 */
export const ANALYTICS_ID_ATTRIBUTE = 'data-analytics-id';

/** Interactive elements a click is attributed to. */
const CLICKABLE_SELECTOR =
  'button, a, summary, [role="button"], [role="menuitem"], [role="tab"], [role="option"], [role="switch"], input[type="submit"], input[type="button"]';

/**
 * Elements whose own text is safe enough to fall back on.
 *
 * Buttons are labelled with verbs written by us ("Save", "Generate images");
 * links are overwhelmingly labelled with the name of the record they point at,
 * which is exactly the data we do not want. So a link contributes its
 * structure and its destination, never its text — unless it opts in with an
 * explicit analytics id.
 */
const TEXT_SAFE_SELECTOR =
  'button, summary, [role="button"], [role="tab"], [role="switch"], input[type="submit"], input[type="button"]';

export interface ElementDescription {
  properties: AnalyticsProperties;
}

/**
 * Find the interactive control that owns a click, unless it sits in an opted-out
 * subtree.
 *
 * The opt-out is checked first and over the whole ancestor chain, not merged
 * into the walk that looks for the control. A button is itself clickable, so a
 * single pass would match it and return before ever noticing the
 * `data-analytics-ignore` on its parent — silently capturing precisely what was
 * marked as off-limits.
 *
 * @param target - The element the click landed on (often an icon inside a button).
 * @returns The owning control, or null when the click hit nothing interactive
 *          or landed inside an ignored subtree.
 */
export function findClickable(target: Element | null): Element | null {
  if (target === null) return null;
  if (target.closest(`[${ANALYTICS_IGNORE_ATTRIBUTE}]`) !== null) return null;

  return target.closest(CLICKABLE_SELECTOR);
}

/**
 * Read the identity of a clicked control.
 *
 * Three sources, in order of how much we trust them: an explicit
 * `data-analytics-id`, an `aria-label` (developer-authored, and required for
 * icon buttons anyway), and finally the element's own text — but only for the
 * element types listed in {@link TEXT_SAFE_SELECTOR}, and always redacted.
 *
 * Form values are never read. Not "redacted": never read. An `<input>`'s value
 * is by definition something a person typed, and no analysis this data serves
 * is worth carrying it.
 *
 * @param element - The interactive control that owns the click.
 * @returns Redacted structural properties describing it.
 */
export function describeElement(element: Element): ElementDescription {
  const properties: AnalyticsProperties = {
    tag: element.tagName.toLowerCase(),
  };

  const role = element.getAttribute('role');
  if (role !== null) properties.role = role;

  const analyticsId = element.getAttribute(ANALYTICS_ID_ATTRIBUTE);
  const ariaLabel = element.getAttribute('aria-label');

  const label =
    analyticsId ??
    ariaLabel ??
    (element.matches(TEXT_SAFE_SELECTOR)
      ? (element.textContent ?? '')
      : '');

  const redacted = redactLabel(label);
  if (redacted !== '') properties.label = redacted;

  // How the label was arrived at, so a later reader can tell a deliberate name
  // from a scraped one without guessing.
  properties.labelSource =
    analyticsId !== null
      ? 'analytics-id'
      : ariaLabel !== null
        ? 'aria-label'
        : redacted !== ''
          ? 'text'
          : 'none';

  return { properties };
}
